import { EyeOutlined } from '@ant-design/icons';
import type { Order } from '@shop-monorepo/types';
import type { TableProps } from 'antd';
import { Button, Empty } from 'antd';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { OrderMobileCard } from '../../components/OrderMobileCard';
import { OrderStatusTag } from '../../components/OrderStatusTag';
import { StyledTable } from '../../components/PageLayout';
import { useIsMobile } from '../../hooks/useIsMobile';
import { formatPrice } from '../../utils/currency';

interface RelatedOrdersByPhoneProps {
  orders: Order[];
}

export const RelatedOrdersByPhone: React.FC<RelatedOrdersByPhoneProps> = ({ orders }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  if (orders.length === 0) {
    return <Empty description="Немає інших замовлень" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const columns: TableProps<Order>['columns'] = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      render: (id: number) => String(id).padStart(4, '0'),
    },
    {
      title: 'Отримувач',
      key: 'recipient',
      render: (_: unknown, record: Order) =>
        `${record.recipient.lastName} ${record.recipient.firstName}`,
    },
    {
      title: 'Дата',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) =>
        new Date(date).toLocaleDateString('uk-UA', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <OrderStatusTag status={status as Order['status']} />,
    },
    {
      title: 'Сума',
      dataIndex: 'total',
      key: 'total',
      render: (total: number) => formatPrice(total),
    },
    {
      key: 'action',
      render: (_: unknown, record: Order) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/orders/${record.id}`)}
        />
      ),
    },
  ];

  if (isMobile) {
    return (
      <>
        {orders.map((order) => (
          <OrderMobileCard
            key={order.id}
            order={order}
            onView={(id) => navigate(`/orders/${id}`)}
          />
        ))}
      </>
    );
  }

  return (
    <StyledTable
      columns={columns}
      dataSource={orders}
      rowKey="id"
      pagination={false}
      size="small"
      scroll={{ x: 650 }}
    />
  );
};
